// <tri-popover> — the Filter button + dropdown on Songs / Recordings /
// Scans. Markup convention (rendered by Components::FilterPopover):
//
//   <tri-popover class="relative inline-block">
//     <button data-popover-trigger aria-expanded="false" aria-haspopup="true">…</button>
//     <div data-popover-panel hidden class="absolute right-0 …">…</div>
//   </tri-popover>
//
// Progressive enhancement: the filter form inside the panel is a plain GET
// form of checkboxes/radios, so nothing here touches filtering itself —
// this element only owns visibility. The trigger toggles the panel; it
// closes on outside click, Escape (focus returns to the trigger), and
// before Turbo caches the page — so restored snapshots never come back
// with a stale open panel.

class TriPopover extends HTMLElement {
	#lifetime: AbortController | null = null;
	#openScope: AbortController | null = null;
	#trigger: HTMLElement | null = null;
	#panel: HTMLElement | null = null;

	connectedCallback() {
		this.#trigger = this.querySelector<HTMLElement>("[data-popover-trigger]");
		this.#panel = this.querySelector<HTMLElement>("[data-popover-panel]");
		if (!this.#trigger || !this.#panel) return;

		this.#lifetime?.abort();
		this.#lifetime = new AbortController();
		const { signal } = this.#lifetime;

		this.#trigger.addEventListener(
			"click",
			(event) => {
				event.stopPropagation();
				this.#open ? this.#close() : this.#show();
			},
			{ signal },
		);

		document.addEventListener("turbo:before-cache", () => this.#close(), {
			signal,
		});
	}

	disconnectedCallback() {
		this.#close();
		this.#lifetime?.abort();
	}

	get #open(): boolean {
		return this.#panel !== null && !this.#panel.hidden;
	}

	#show() {
		if (!this.#panel || !this.#trigger) return;

		this.#panel.hidden = false;
		this.#trigger.setAttribute("aria-expanded", "true");

		// Document-level listeners live only while the panel is open.
		this.#openScope = new AbortController();
		const { signal } = this.#openScope;

		document.addEventListener(
			"click",
			(event) => {
				if (event.target instanceof Node && !this.contains(event.target)) {
					this.#close();
				}
			},
			{ signal },
		);

		document.addEventListener(
			"keydown",
			(event) => {
				if (event.key === "Escape") {
					this.#close();
					this.#trigger?.focus();
				}
			},
			{ signal },
		);
	}

	#close() {
		if (!this.#open) return;

		if (this.#panel) this.#panel.hidden = true;
		this.#trigger?.setAttribute("aria-expanded", "false");
		this.#openScope?.abort();
		this.#openScope = null;
	}
}

if (!customElements.get("tri-popover")) {
	customElements.define("tri-popover", TriPopover);
}
